import React, { useCallback, useEffect, useState } from "react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../components/ui/sheet";
import {
  ArrowLeft, Clock, Users, AlertTriangle, Activity as ActivityIcon, Eye, Search, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { tr } from "../i18n/LangContext";

const hrs = (mins) => (mins >= 60 ? `${(mins / 60).toFixed(1)}h` : `${mins || 0}m`);
const ago = (iso) => {
  if (!iso) return tr("never");
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return tr("today");
  if (days === 1) return "yesterday";
  return `${days} days ago`;
};

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className={`rounded-2xl border p-4 ${tone === "red" ? "border-brand-red/25 bg-brand-red/[0.05]" : "border-white/[0.07] bg-card/40"}`}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
      <Icon className={`h-3.5 w-3.5 ${tone === "red" ? "text-brand-red" : "text-brand-green"}`} /> {tr(label)}
    </div>
    <p className="mt-1.5 font-display text-2xl font-semibold text-white">{value}</p>
  </div>
);

function StudentSheet({ uid, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!uid) return;
    setD(null);
    client.get(`/activity/student/${uid}`)
      .then((r) => setD(r.data))
      .catch((e) => toast.error(tr("Couldn't load activity"), { description: e?.response?.data?.detail }));
  }, [uid]);
  // Fill the full 30-day window so quiet days read as gaps, not missing bars.
  const series = React.useMemo(() => {
    const byDate = Object.fromEntries((d?.daily || []).map((x) => [x.date, x]));
    return Array.from({ length: 30 }, (_, i) => {
      const dt = new Date(Date.now() - (29 - i) * 86400000).toISOString().slice(0, 10);
      return byDate[dt] || { date: dt, minutes: 0, visits: 0 };
    });
  }, [d]);
  const peak = Math.max(1, ...series.map((x) => x.minutes));
  return (
    <Sheet open={!!uid} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto border-white/10 bg-[#0a0e12] sm:max-w-xl" data-testid="activity-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-xl text-white">{d?.student?.name || "Student"}</SheetTitle>
          <SheetDescription>
            {d ? `${d.student.email} · last signed in ${ago(d.last_login)}` : "Loading…"}
          </SheetDescription>
        </SheetHeader>

        {d && (
          <div className="mt-6 space-y-8">
            <div className="grid grid-cols-3 gap-3">
              <Stat icon={Clock} label={tr("This month")} value={hrs(d.minutes_month)} />
              <Stat icon={ActivityIcon} label={tr("Visits")} value={d.visits_month} />
              <Stat icon={CalendarDays} label={tr("All time")} value={hrs(d.minutes_total)} />
            </div>

            <div>
              <h4 className="font-display text-sm font-semibold text-white">{tr("Last 30 days")}</h4>
              {d.daily.length === 0 && (
                <p className="mt-1 text-xs text-muted-foreground">{tr("Nothing in the last 30 days.")}</p>
              )}
              {(
                <div className="mt-3 flex h-[100px] items-end gap-1" data-testid="activity-daily-chart">
                  {series.map((x) => (
                    <div key={x.date} className="group flex-1"
                      title={x.minutes ? `${x.date}: ${hrs(x.minutes)} over ${x.visits} visit(s)` : `${x.date}: nothing`}>
                      <div className={`rounded-t transition-colors ${x.minutes ? "bg-brand-green/70 group-hover:bg-brand-green" : "bg-white/[0.07]"}`}
                        style={{ height: `${x.minutes ? Math.max(6, (x.minutes / peak) * 92) : 3}px` }} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h4 className="font-display text-sm font-semibold text-white">{tr("Who from the team opened this workspace")}</h4>
              {d.team_access.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">{tr("Nobody has opened it yet.")}</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {d.team_access.map((t) => (
                    <div key={t.user_id} data-testid="team-access-row"
                      className="flex items-center justify-between rounded-xl border border-white/[0.07] bg-white/[0.02] px-4 py-2.5">
                      <div>
                        <p className="text-sm text-white">{t.name || t.email}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {t.role === "super_admin" ? "Super admin" : "Success manager"} · last {ago(t.last_at)}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground">{t.visits} visit{t.visits === 1 ? "" : "s"} · {hrs(t.minutes)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h4 className="font-display text-sm font-semibold text-white">{tr("Recent sign-ins")}</h4>
              <div className="mt-3 space-y-1.5">
                {d.logins.length === 0 && <p className="text-sm text-muted-foreground">{tr("No sign-ins recorded yet.")}</p>}
                {d.logins.slice(0, 12).map((l) => (
                  <div key={l.at} className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{new Date(l.at).toLocaleString()}</span>
                    <span className="uppercase tracking-wide">{l.provider}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function Activity() {
  const { stopActing, user } = useAuth();
  const [data, setData] = useState(null);
  const [access, setAccess] = useState([]);
  const [manager, setManager] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [tab, setTab] = useState("students");

  const load = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([client.get("/activity/students"), client.get("/activity/access")]);
      setData(s.data);
      setAccess(a.data.rows || []);
    } catch (e) {
      toast.error(tr("Couldn't load activity"), { description: e?.response?.data?.detail });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const back = () => { stopActing(); window.location.assign("/admin"); };
  const students = (data?.students || []).filter((s) => {
    const mids = [s.assigned_admin_id, ...(s.support_admin_ids || [])].filter(Boolean);
    if (manager === "none" ? mids.length > 0 : manager !== "all" && !mids.includes(manager)) return false;
    const hay = `${s.name || ""} ${s.email || ""}`.toLowerCase();
    return !q.trim() || hay.includes(q.trim().toLowerCase());
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-white/[0.06] bg-[#0a0e12]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
          <Button variant="outline" onClick={back} data-testid="activity-back"
            className="h-10 border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">
            <ArrowLeft className="h-4 w-4" /> Console
          </Button>
          <img src={BRAND.logo} alt="Moroccan Brothers" className="h-10 w-auto object-contain" />
          <div className="mr-auto">
            <h1 className="font-display text-lg font-semibold text-white">{tr("Student activity")}</h1>
            <p className="text-xs text-muted-foreground">{tr("Sign-ins, time in the app, and who opened which workspace")}</p>
          </div>
          <div className="flex rounded-xl border border-white/10 bg-white/[0.03] p-1">
            {[["students", "Students"], ["access", "Team access"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`activity-tab-${k}`}
                className={`rounded-lg px-4 py-1.5 text-xs font-semibold transition-colors ${
                  tab === k ? "bg-brand-red text-white" : "text-muted-foreground hover:text-white"}`}>
                {tr(l)}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Users} label={tr("Active students")} value={data?.totals?.students ?? "—"} />
          <Stat icon={ActivityIcon} label={tr("Used it this month")} value={data?.totals?.active_month ?? "—"} />
          <Stat icon={Clock} label={tr("Hours this month")} value={data ? `${data.totals.hours_month}h` : "—"} />
          <Stat icon={AlertTriangle} tone="red" label={`${tr("Quiet")} ${data?.stale_days ?? 10}+ ${tr("days")}`} value={data?.totals?.stale ?? "—"} />
        </div>

        {tab === "students" ? (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="activity-search"
                  placeholder={tr("Search a student…")} className="h-10 border-white/10 bg-input/50 pl-9" />
              </div>
              <button onClick={() => setManager(manager === user?.user_id ? "all" : user?.user_id)}
                data-testid="activity-my-students"
                className={`h-10 rounded-xl border px-4 text-xs font-semibold transition-colors ${
                  manager === user?.user_id
                    ? "border-brand-green/35 bg-brand-green/10 text-brand-green"
                    : "border-white/10 bg-white/[0.03] text-muted-foreground hover:text-white"}`}>
                {tr("My students")}
              </button>
              <Select value={manager} onValueChange={setManager}>
                <SelectTrigger className="h-10 w-[230px] border-white/10 bg-input/50" data-testid="activity-manager-filter">
                  <SelectValue placeholder={tr("All success managers")} />
                </SelectTrigger>
                <SelectContent className="glass-strong border-white/10">
                  <SelectItem value="all">{tr("All success managers")}</SelectItem>
                  <SelectItem value="none">{tr("Unassigned")}</SelectItem>
                  {(data?.managers || []).map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-white/[0.07] bg-card/40">
              <table className="w-full min-w-[820px] text-left text-sm">
                <thead>
                  <tr className="border-b border-white/[0.06] text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-5 py-3">{tr("Student")}</th>
                    <th className="px-5 py-3">{tr("Last sign-in")}</th>
                    <th className="px-5 py-3">{tr("Sign-ins (month)")}</th>
                    <th className="px-5 py-3">{tr("Visits (month)")}</th>
                    <th className="px-5 py-3">{tr("Time (month)")}</th>
                    <th className="px-5 py-3">{tr("Time (all time)")}</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={s.user_id} data-testid="activity-row"
                      className={`border-b border-white/[0.04] transition-colors hover:bg-white/[0.03] ${s.stale ? "bg-brand-red/[0.04]" : ""}`}>
                      <td className="px-5 py-3">
                        <p className="font-medium text-white">{s.name || s.email}</p>
                        <p className="text-[11px] text-muted-foreground">{s.email}</p>
                      </td>
                      <td className="px-5 py-3">
                        <span className={s.stale ? "text-brand-red" : "text-slate-300"} data-testid="activity-last-seen">{ago(s.last_login)}</span>
                        {s.stale && (
                          <span className="ml-2 inline-flex items-center gap-1 rounded-md border border-brand-red/30 bg-brand-red/10 px-1.5 py-0.5 text-[10px] text-brand-red" data-testid="activity-stale-flag">
                            <AlertTriangle className="h-3 w-3" /> {tr("chase")}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-slate-300">{s.logins_month}</td>
                      <td className="px-5 py-3 text-slate-300">{s.visits_month}</td>
                      <td className="px-5 py-3 font-medium text-white">{hrs(s.minutes_month)}</td>
                      <td className="px-5 py-3 text-slate-300">{hrs(s.minutes_total)}</td>
                      <td className="px-5 py-3 text-right">
                        <Button variant="outline" size="sm" onClick={() => setOpen(s.user_id)} data-testid="activity-view-btn"
                          className="border-white/12 bg-white/[0.03] text-xs text-white hover:bg-white/[0.07]">
                          <Eye className="h-3.5 w-3.5" /> {tr("Detail")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {students.length === 0 && (
                    <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">{tr("No students match that filter.")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-white/[0.07] bg-card/40">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/[0.06] text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-5 py-3">{tr("Team member")}</th>
                  <th className="px-5 py-3">{tr("Opened")}</th>
                  <th className="px-5 py-3">{tr("When")}</th>
                  <th className="px-5 py-3">{tr("Time in there")}</th>
                </tr>
              </thead>
              <tbody>
                {access.map((r) => (
                  <tr key={`${r.student_id}-${r.started_at}`} data-testid="access-row" className="border-b border-white/[0.04] hover:bg-white/[0.03]">
                    <td className="px-5 py-3">
                      <p className="font-medium text-white">{r.actor_name || r.actor_email}</p>
                      <p className="text-[11px] text-muted-foreground">{r.actor_role === "super_admin" ? "Super admin" : "Success manager"}</p>
                    </td>
                    <td className="px-5 py-3 text-slate-300">{r.student_name}</td>
                    <td className="px-5 py-3 text-slate-300">{new Date(r.started_at).toLocaleString()}</td>
                    <td className="px-5 py-3 text-white">{hrs(r.minutes)}</td>
                  </tr>
                ))}
                {access.length === 0 && (
                  <tr><td colSpan={4} className="px-5 py-10 text-center text-muted-foreground">{tr("Nobody has opened a student workspace yet.")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <StudentSheet uid={open} onClose={() => setOpen(null)} />
    </div>
  );
}
